// Main-thread controller for the browser PCM capture pipeline — see
// docs/specs/webstream-1.md §2c. Wires getUserMedia -> AudioContext -> AudioWorkletNode, and
// hands each worklet-accumulated Float32Array buffer to `encodePcmFrame` for resample + Int16
// encoding before invoking `onFrame`. Not unit-tested here (Web Audio APIs are absent from
// jsdom) — exercised end-to-end by WEBSTREAM-4 against a fake mic device.
import { CaptureError } from '@scribe/core/errors';
import { encodePcmFrame } from './pcmEncode';

export interface PcmCaptureHandle {
  /** Stop capture: disconnect nodes, close the AudioContext, and stop mic tracks THIS module
   * started (a caller-supplied `stream` is left alone). Idempotent. */
  stop(): Promise<void>;
  /** The AudioContext's actual sample rate (may differ from 16000 if the browser ignored it). */
  readonly contextSampleRate: number;
}

export interface PcmCaptureOptions {
  /** Called with each ready 16 kHz mono Int16 LE frame (~100 ms). */
  onFrame: (pcm: ArrayBuffer) => void;
  onError?: (err: unknown) => void;
  /** ERRORS-1: the input device went away mid-capture (a track fired `ended` — unplugged,
   * Bluetooth dropped, OS revoked access). Informational: capture is NOT torn down here, so
   * the caller can still Stop cleanly and keep what was recorded. */
  onDeviceLost?: () => void;
  /** Inject a MediaStream (tests/e2e fake device). Default: getUserMedia({audio:{channelCount:1}}). */
  stream?: MediaStream;
  /** Override the worklet module URL (tests). Default resolves ./pcm-worklet.js via import.meta.url. */
  workletUrl?: string | URL;
}

export async function startPcmCapture(opts: PcmCaptureOptions): Promise<PcmCaptureHandle> {
  // ERRORS-1: name the unsupported-platform cases instead of letting them surface as an opaque
  // TypeError (which the record screen used to report as a network problem). Both APIs are
  // absent on insecure (http) origins as well as in old browsers.
  if (!opts.stream && !navigator.mediaDevices?.getUserMedia) {
    throw new CaptureError('capture_unsupported', 'getUserMedia unavailable');
  }
  if (typeof AudioContext === 'undefined') {
    throw new CaptureError('capture_unsupported', 'AudioContext unavailable');
  }
  const stream =
    opts.stream ??
    (await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    }));

  const ctx = new AudioContext({ sampleRate: 16000 }); // request 16 kHz (most browsers honor it)
  if (!ctx.audioWorklet) {
    await ctx.close().catch(() => {});
    if (!opts.stream) stream.getTracks().forEach((t) => t.stop());
    throw new CaptureError('capture_unsupported', 'AudioWorklet unavailable');
  }

  await ctx.audioWorklet.addModule(opts.workletUrl ?? new URL('./pcm-worklet.js', import.meta.url));

  const frameSamples = Math.round(ctx.sampleRate * 0.1); // ~100 ms at the real rate

  const source = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, 'pcm-capture', { processorOptions: { frameSamples } });

  node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
    try {
      opts.onFrame(encodePcmFrame(new Float32Array(e.data), ctx.sampleRate, 16000));
    } catch (err) {
      opts.onError?.(err);
    }
  };

  // The worklet writes no output, so the destination receives silence (no mic-to-speaker
  // feedback), but the connection is required for the graph to pull audio through the node.
  source.connect(node);
  node.connect(ctx.destination);

  let stopped = false;
  const onEnded = () => {
    if (!stopped) opts.onDeviceLost?.();
  };
  const tracks = stream.getAudioTracks?.() ?? [];
  tracks.forEach((t) => t.addEventListener('ended', onEnded));

  return {
    contextSampleRate: ctx.sampleRate,
    async stop() {
      if (stopped) return;
      stopped = true;
      tracks.forEach((t) => t.removeEventListener('ended', onEnded));
      node.port.onmessage = null;
      node.disconnect();
      source.disconnect();
      await ctx.close();
      if (!opts.stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    },
  };
}
